import React, { useState, useRef, useMemo } from 'react';
import Breadcrumb from '../../components/common/Breadcrumb';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  IconUpload, IconDownload, IconEdit, IconTrash, IconPlus,
  IconAlertTriangle, IconFileSpreadsheet, IconX, IconCheck, IconChevronRight,
} from '@tabler/icons-react';
import {
  usePayrollTemplate,
  usePayrollTemplateRecords,
  useUploadPayrollTemplate,
  useUpdateTemplateRecord,
  useAddTemplateRecord,
  useDeleteTemplateRecord,
  useDeletePayrollTemplate,
  useDownloadPayrollTemplate,
  usePayrollRuns,
} from '../../hooks/usePayroll';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import ActionDropdown from '../../components/common/ActionDropdown';
import { PageLoader, TableLoader } from '../../components/common/Loader';

const fmt = (v) =>
  Number(v || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SALARY_FIELDS = [
  { key: 'basic_salary', label: 'Basic Salary' },
  { key: 'housing', label: 'Housing' },
  { key: 'transport', label: 'Transport' },
  { key: 'meal', label: 'Meal' },
  { key: 'utility', label: 'Utility' },
  { key: 'entertainment', label: 'Entertainment' },
  { key: 'other_non_allowable', label: 'Other Non-Allowable' },
  { key: 'leave_allowance', label: 'Leave Allowance' },
];

const DEDUCTION_FIELDS = [
  { key: 'pension_employee', label: 'Employee Pension' },
  { key: 'pension_employer', label: 'Employer Pension' },
  { key: 'paye_tax', label: 'PAYE Tax' },
  { key: 'nhf', label: 'NHF' },
  { key: 'nhis', label: 'NHIS' },
  { key: 'life_assurance', label: 'Life Assurance' },
  { key: 'loan_deduction', label: 'Loan Deduction' },
  { key: 'other_deductions', label: 'Other Deductions' },
];

const emptyRecordForm = () => ({
  employee: '',
  basic_salary: '',
  housing: '',
  transport: '',
  meal: '',
  utility: '',
  entertainment: '',
  other_non_allowable: '',
  leave_allowance: '',
  pension_employee: '',
  pension_employer: '',
  paye_tax: '',
  nhf: '',
  nhis: '',
  life_assurance: '',
  loan_deduction: '',
  other_deductions: '',
  months_worked: 12,
});

function RecordModal({ isOpen, onClose, record, orgId, employees }) {
  const isNew = !record;
  const updateRecord = useUpdateTemplateRecord();
  const addRecord = useAddTemplateRecord();

  const [form, setForm] = useState(() => {
    if (record) {
      const f = {};
      [...SALARY_FIELDS, ...DEDUCTION_FIELDS].forEach(({ key }) => {
        f[key] = record[key] ?? '';
      });
      f.months_worked = record.months_worked ?? 12;
      f.employee = record.employee ?? '';
      return f;
    }
    return emptyRecordForm();
  });

  if (!isOpen) return null;

  const handleChange = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = () => {
    const data = { ...form };
    // Remove empty strings for optional fields
    Object.keys(data).forEach((k) => {
      if (data[k] === '') delete data[k];
    });

    if (isNew) {
      if (!form.employee) {
        toast.error('Please select an employee.');
        return;
      }
      if (!form.basic_salary) {
        toast.error('Basic salary is required.');
        return;
      }
      addRecord.mutate({ data, orgId }, {
        onSuccess: () => { toast.success('Record added.'); onClose(); },
        onError: (err) => toast.error(err?.response?.data?.detail || 'Failed to add record.'),
      });
    } else {
      updateRecord.mutate({ id: record.id, data, orgId }, {
        onSuccess: () => { toast.success('Record updated.'); onClose(); },
        onError: (err) => toast.error(err?.response?.data?.detail || 'Failed to update record.'),
      });
    }
  };

  const isPending = updateRecord.isPending || addRecord.isPending;

  return (
    <>
      <div className="fixed inset-0 z-[1001] flex items-center justify-center p-15">
        <div className="bg-bg-dark rounded-lg shadow-light border border-border w-full max-w-[700px] max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-20 border-b border-border sticky top-0 bg-bg-dark z-10">
            <h3 className="text-[16px]/[20px] font-semibold">
              {isNew ? 'Add Employee Record' : `Edit — ${record.raw_name || record.employee_name}`}
            </h3>
            <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
              <IconX size={20} />
            </button>
          </div>

          <div className="p-20 flex flex-col gap-20">
            {/* Employee selector (new record or unmatched) */}
            {(isNew || !record?.employee) && (
              <div>
                <label className="block text-sm font-medium mb-5">
                  Employee {isNew && <span className="text-danger">*</span>}
                </label>
                <select
                  value={form.employee}
                  onChange={(e) => handleChange('employee', e.target.value)}
                  className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none"
                >
                  <option value="">Select employee...</option>
                  {(employees || []).map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name} ({emp.employeeId})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Months worked */}
            <div className="grid grid-cols-2 gap-10">
              <div>
                <label className="block text-sm font-medium mb-5">Months Worked</label>
                <input
                  type="number"
                  min={1}
                  max={12}
                  value={form.months_worked}
                  onChange={(e) => handleChange('months_worked', e.target.value)}
                  className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none"
                />
              </div>
            </div>

            {/* Earnings */}
            <div>
              <p className="text-sm font-semibold text-success mb-10">Earnings</p>
              <div className="grid grid-cols-2 gap-10">
                {SALARY_FIELDS.map(({ key, label }) => (
                  <div key={key}>
                    <label className="block text-xs text-text-secondary mb-3">
                      {label} {key === 'basic_salary' && isNew && <span className="text-danger">*</span>}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={form[key]}
                      onChange={(e) => handleChange(key, e.target.value)}
                      placeholder="0.00"
                      className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[6px] text-sm focus:outline-none"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Deductions */}
            <div>
              <p className="text-sm font-semibold text-danger mb-10">Deductions</p>
              <div className="grid grid-cols-2 gap-10">
                {DEDUCTION_FIELDS.map(({ key, label }) => (
                  <div key={key}>
                    <label className="block text-xs text-text-secondary mb-3">{label}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={form[key]}
                      onChange={(e) => handleChange(key, e.target.value)}
                      placeholder="0.00"
                      className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[6px] text-sm focus:outline-none"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-10 p-20 border-t border-border sticky bottom-0 bg-bg-dark">
            <button onClick={onClose} className="btn btn-outline-secondary flex-1" disabled={isPending}>
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isPending}
              className="btn btn-primary flex-1 flex items-center justify-center gap-5"
            >
              <IconCheck size={16} />
              {isPending ? 'Saving...' : 'Save Record'}
            </button>
          </div>
        </div>
      </div>
      <div onClick={onClose} className="fixed z-[1000] inset-0 bg-black/50 backdrop-blur-[3px]" />
    </>
  );
}

export default function PayrollTemplate() {
  const orgId = localStorage.getItem('orgId') || '';
  const { hasAnyPermission } = useAuth();

  const canUpload = hasAnyPermission(['rbacapp.pay_upload']);
  // HR both uploads the template and reviews it, so holding pay_upload must not hide the
  // review view — the uploader is the one who chases up rows that failed to match.
  const canReview = hasAnyPermission(['rbacapp.pay_review']);

  const navigate = useNavigate();

  const fileInputRef = useRef(null);
  const [uploadForm, setUploadForm] = useState({ label: '', notes: '', file: null });
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [showUnmatched, setShowUnmatched] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [addingRecord, setAddingRecord] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmDeleteTemplate, setConfirmDeleteTemplate] = useState(false);
  const [forceDeleteTemplate, setForceDeleteTemplate] = useState(false);
  const [justUploaded, setJustUploaded] = useState(false);
  const [expandAll, setExpandAll] = useState(false);

  const { data: template, isLoading: loadingTemplate } = usePayrollTemplate(orgId, { enabled: canReview });
  const { data: runs } = usePayrollRuns(orgId, { enabled: canReview });

  // A run in any of these states holds a snapshot that is still moving through approval,
  // so the template below it is just reference material — collapse it out of the way.
  // Published/rejected runs are done with, so the page goes back to normal for next cycle.
  const activeRun = useMemo(
    () => (runs || []).find((r) => ['draft', 'submitted', 'approved'].includes(r.status)),
    [runs]
  );

  // While a run is in flight we only surface unmatched rows — those are the ones that
  // snapshotted into the run without an employee and so will get no payslip.
  const collapsed = !!activeRun && !expandAll;

  const { data: records, isLoading: loadingRecords } = usePayrollTemplateRecords(orgId, {
    unmatched: collapsed ? true : showUnmatched,
    enabled: canReview,
  });

  const uploadTemplate = useUploadPayrollTemplate();
  const deleteRecord = useDeleteTemplateRecord();
  const deleteTemplate = useDeletePayrollTemplate();
  const downloadTemplate = useDownloadPayrollTemplate();

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xlsm)$/i)) {
      toast.error('Only .xlsx or .xlsm files are accepted.');
      return;
    }
    setUploadForm((prev) => ({ ...prev, file }));
  };

  const handleUpload = () => {
    if (!uploadForm.file) { toast.error('Please select an Excel file.'); return; }
    if (!uploadForm.label.trim()) { toast.error('Label is required.'); return; }

    uploadTemplate.mutate(
      { file: uploadForm.file, label: uploadForm.label.trim(), notes: uploadForm.notes, orgId },
      {
        onSuccess: (data) => {
          toast.success(data.message || 'Template uploaded successfully.');
          if (data.unmatched_count > 0) {
            toast(`${data.unmatched_count} row(s) could not be matched to system employees. Review unmatched records.`, { icon: '⚠️' });
          }
          setUploadForm({ label: '', notes: '', file: null });
          setShowUploadForm(false);
          setJustUploaded(true);
          if (fileInputRef.current) fileInputRef.current.value = '';
        },
        onError: (err) => toast.error(err?.response?.data?.detail || 'Upload failed.'),
      }
    );
  };

  const handleDelete = () => {
    deleteRecord.mutate({ id: deleteTarget.id, orgId }, {
      onSuccess: () => { toast.success('Record removed.'); setDeleteTarget(null); },
      onError: () => { toast.error('Failed to remove record.'); setDeleteTarget(null); },
    });
  };

  const handleDeleteTemplate = (force = false) => {
    deleteTemplate.mutate({ orgId, force }, {
      onSuccess: (data) => {
        toast.success(data?.message || 'Template deleted.');
        setConfirmDeleteTemplate(false);
        setForceDeleteTemplate(false);
      },
      onError: (err) => {
        const status = err?.response?.status;
        if (status === 400 && !force) {
          setConfirmDeleteTemplate(false);
          setForceDeleteTemplate(true);
          return;
        }
        const fallback = status === 403
          ? 'You do not have permission to delete the template.'
          : status === 404
            ? 'No template found.'
            : 'Failed to delete template.';
        toast.error(err?.response?.data?.error || err?.response?.data?.detail || fallback);
        setConfirmDeleteTemplate(false);
        setForceDeleteTemplate(false);
      },
    });
  };

  return (
    <>
      <Breadcrumb title="Payroll Template" path="Payroll / Template" />

      {/* Upload Section — Finance Manager (pay_upload) */}
      {canUpload && (
        <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15 mb-20">
          <div className="flex items-start justify-between gap-15 flex-wrap">
            <div>
              <h2 className="text-[16px]/[20px] font-semibold mb-5">Upload Payroll Template</h2>
              <p className="text-text-secondary text-sm">
                Upload the Excel file containing all employee salary data for this payroll period.
              </p>
            </div>
            <button
              onClick={() => setShowUploadForm((prev) => !prev)}
              className="btn btn-primary flex items-center gap-5"
            >
              <IconUpload size={16} />
              {showUploadForm ? 'Cancel' : 'Upload Template'}
            </button>
          </div>

          {showUploadForm && (
            <div className="mt-20 pt-20 border-t border-border">
              <div className="grid md:grid-cols-2 grid-cols-1 gap-15 mb-15">
                <div>
                  <label className="block text-sm mb-5">
                    Label <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    value={uploadForm.label}
                    onChange={(e) => setUploadForm((p) => ({ ...p, label: e.target.value }))}
                    placeholder="e.g. March 2026 Payroll"
                    className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-5">Notes (optional)</label>
                  <input
                    type="text"
                    value={uploadForm.notes}
                    onChange={(e) => setUploadForm((p) => ({ ...p, notes: e.target.value }))}
                    placeholder="Any notes..."
                    className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none"
                  />
                </div>
              </div>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-10 border-2 border-dashed border-border rounded-lg p-15 cursor-pointer hover:border-primary transition-colors bg-bg-light mb-15"
              >
                <IconFileSpreadsheet size={20} className="text-success" />
                <span className="text-text-secondary text-sm">
                  {uploadForm.file ? uploadForm.file.name : 'Click to select .xlsx or .xlsm file'}
                </span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xlsm"
                className="hidden"
                onChange={handleFileSelect}
              />
              <div className="flex gap-10">
                <button
                  onClick={() => setShowUploadForm(false)}
                  className="btn btn-outline-secondary"
                  disabled={uploadTemplate.isPending}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploadTemplate.isPending}
                  className="btn btn-primary flex items-center gap-5"
                >
                  <IconUpload size={16} />
                  {uploadTemplate.isPending ? 'Uploading...' : 'Upload & Replace'}
                </button>
              </div>
              <p className="text-xs text-warning flex items-center gap-5 mt-10">
                <IconAlertTriangle size={14} />
                Uploading a new file will replace ALL existing template records.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Template Summary — HR Manager (pay_review) */}
      {canReview && (
        <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15 mb-20">
          <div className="flex items-start justify-between gap-15 flex-wrap">
            <div>
              <h2 className="text-[16px]/[20px] font-semibold mb-5">Active Template</h2>
              {loadingTemplate ? (
                <p className="text-text-secondary text-sm">Loading...</p>
              ) : template && template.id ? (
                <div className="flex flex-col gap-5">
                  <p className="font-medium">{template.label}</p>
                  <p className="text-text-secondary text-sm">{template.original_filename}</p>
                  <div className="flex gap-20 mt-5">
                    <span className="text-sm">
                      <span className="text-text-secondary">Total rows:</span>{' '}
                      <strong>{template.row_count}</strong>
                    </span>
                    <span className="text-sm">
                      <span className="text-success">Matched:</span>{' '}
                      <strong>{template.matched_count}</strong>
                    </span>
                    {template.unmatched_count > 0 && (
                      <span className="text-sm">
                        <span className="text-warning">Unmatched:</span>{' '}
                        <strong>{template.unmatched_count}</strong>
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-text-secondary mt-5">
                    Uploaded by {template.uploaded_by_name} &bull;{' '}
                    {new Date(template.created_at).toLocaleDateString('en-GB', {
                      day: '2-digit', month: 'short', year: 'numeric',
                    })}
                  </p>
                </div>
              ) : (
                <p className="text-text-secondary text-sm">No template uploaded yet. Ask Finance to upload one.</p>
              )}
            </div>
            {template?.id && (
              <button
                onClick={() => downloadTemplate.mutate({ orgId, label: template.label })}
                disabled={downloadTemplate.isPending}
                className="btn btn-outline-secondary flex items-center gap-5"
              >
                <IconDownload size={16} />
                {downloadTemplate.isPending ? 'Downloading...' : 'Download Excel'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Records Table */}
      {canReview && (
        <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
          <div className="flex items-center justify-between gap-15 flex-wrap mb-15">
            <div>
              <h2 className="text-[16px]/[20px] font-semibold">
                {collapsed ? 'Unmatched Records' : 'Employee Records'}
              </h2>
              {template?.unmatched_count > 0 && (
                <p className="text-xs text-warning mt-3">
                  {collapsed
                    ? `${template.unmatched_count} unmatched row(s) won't receive a payslip`
                    : `${template.unmatched_count} unmatched row(s) need attention`}
                </p>
              )}
            </div>
            <div className="flex gap-10 flex-wrap items-center">
              {collapsed ? (
                <button
                  onClick={() => setExpandAll(true)}
                  className="btn btn-outline-secondary"
                >
                  Show all {template?.row_count || ''} records
                </button>
              ) : (
                <>
                  {activeRun && (
                    <button
                      onClick={() => setExpandAll(false)}
                      className="btn btn-outline-secondary"
                    >
                      Hide matched
                    </button>
                  )}
                  <label className="flex items-center gap-5 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showUnmatched}
                      onChange={(e) => setShowUnmatched(e.target.checked)}
                      className="rounded"
                    />
                    Show unmatched only
                  </label>
                  <button
                    onClick={() => setAddingRecord(true)}
                    className="btn btn-outline-secondary flex items-center gap-5"
                  >
                    <IconPlus size={16} />
                    Add Manually
                  </button>
                </>
              )}
            </div>
          </div>

          {loadingRecords ? (
            <TableLoader text="Loading records..." />
          ) : !records?.length ? (
            <TableLoader text={showUnmatched ? 'No unmatched records.' : 'No records found. Upload an Excel template to get started.'} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr className="bg-bg-light-100">
                    <th align="left" className="p-10 font-medium">Employee</th>
                    <th align="left" className="p-10 font-medium">Position</th>
                    <th align="right" className="p-10 font-medium">Basic Salary</th>
                    <th align="right" className="p-10 font-medium">Gross Pay</th>
                    <th align="right" className="p-10 font-medium">Net Pay</th>
                    <th align="center" className="p-10 font-medium">Status</th>
                    <th align="center" className="p-10 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((row) => (
                    <tr key={row.id} className="border-t border-border hover:bg-bg-light">
                      <td className="p-10">
                        <p className="font-medium">{row.employee_name || row.raw_name}</p>
                        <p className="text-xs text-text-secondary">{row.raw_email}</p>
                      </td>
                      <td className="p-10 text-text-secondary">{row.raw_position || '—'}</td>
                      <td className="p-10 text-right">₦{fmt(row.basic_salary)}</td>
                      <td className="p-10 text-right">₦{fmt(row.gross_pay)}</td>
                      <td className="p-10 text-right font-medium">₦{fmt(row.net_pay)}</td>
                      <td className="p-10 text-center">
                        {row.employee ? (
                          <span className="px-8 py-3 rounded-full text-xs bg-success/10 text-success">Matched</span>
                        ) : (
                          <span className="px-8 py-3 rounded-full text-xs bg-warning/10 text-warning">Unmatched</span>
                        )}
                        {row.is_manually_edited && (
                          <span className="ml-5 px-8 py-3 rounded-full text-xs bg-sky/10 text-sky">Edited</span>
                        )}
                      </td>
                      <td className="p-10">
                        <div className="flex items-center justify-center">
                          <ActionDropdown
                            menuItems={[
                              {
                                label: 'Edit Record',
                                icon: IconEdit,
                                description: 'Update salary figures for this employee',
                                onClick: () => setEditRecord(row),
                              },
                              {
                                label: 'Remove',
                                icon: IconTrash,
                                danger: true,
                                description: 'Remove this record from the template',
                                onClick: () => setDeleteTarget(row),
                              },
                              ...(canUpload ? [{
                                label: 'Delete Template',
                                icon: IconTrash,
                                danger: true,
                                description: 'Delete the whole active template, all its records, and the stored Excel',
                                onClick: () => setConfirmDeleteTemplate(true),
                              }] : []),
                            ]}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        </div>
      )}

      {/* Next step — one CTA, at the end of the flow, once a template actually exists.
          Upload-only accounts have no template query, so justUploaded stands in for them. */}
      {(template?.id || justUploaded) && (
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-15 mt-20 flex items-center justify-between gap-15 flex-wrap">
          <div>
            <p className="font-medium text-sm">Ready to run payroll?</p>
            <p className="text-text-secondary text-xs mt-2">
              Once the records above look right, go to Payroll Runs to trigger a run and assign a reviewer and approver.
            </p>
          </div>
          <button
            onClick={() => navigate('/payroll/runs')}
            className="btn btn-primary flex items-center gap-5 whitespace-nowrap"
          >
            Go to Payroll Runs
            <IconChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Edit Modal */}
      <RecordModal
        isOpen={!!editRecord}
        onClose={() => setEditRecord(null)}
        record={editRecord}
        orgId={orgId}
      />

      {/* Add Modal */}
      <RecordModal
        isOpen={addingRecord}
        onClose={() => setAddingRecord(false)}
        record={null}
        orgId={orgId}
      />

      {/* Delete Confirmation */}
      <ConfirmDeleteModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        isLoading={deleteRecord.isPending}
        title="Remove Record"
        message={`Remove ${deleteTarget?.employee_name || deleteTarget?.raw_name || 'this employee'} from the template? This cannot be undone.`}
        confirmLabel="Remove"
        loadingLabel="Removing..."
      />

      {/* Delete Template Confirmation */}
      <ConfirmDeleteModal
        isOpen={confirmDeleteTemplate}
        onClose={() => setConfirmDeleteTemplate(false)}
        onConfirm={() => handleDeleteTemplate(false)}
        isLoading={deleteTemplate.isPending}
        title="Delete Template"
        message={`Delete the active template "${template?.label}"? This removes the template, all its records, and the stored Excel file. This cannot be undone.`}
        confirmLabel="Delete Template"
        loadingLabel="Deleting..."
      />

      {/* Force Delete — runs still reference the template */}
      <ConfirmDeleteModal
        isOpen={forceDeleteTemplate}
        onClose={() => setForceDeleteTemplate(false)}
        onConfirm={() => handleDeleteTemplate(true)}
        isLoading={deleteTemplate.isPending}
        title="Payroll Runs Reference This Template"
        message="One or more payroll runs still reference this template. Force-deleting will remove it anyway — those runs keep their own snapshot data, but you won't be able to trace them back to this template. Continue?"
        confirmLabel="Force Delete"
        loadingLabel="Deleting..."
      />

    </>
  );
}
